/**
 * Which WhatsApp channel a NEW contact is added on. A workspace can have the official Meta number and up to 5 QR
 * numbers; a QR contact belongs to one number (platform "qr" + connectionId), so adding one needs that choice:
 *   - Meta number + QR numbers  -> pick "official" or "QR" (+ which QR number)
 *   - QR numbers only           -> QR (+ which number when there are several)
 *   - otherwise                 -> nothing to choose; the contact is added exactly as before (Meta).
 * QR options appear only for accounts staff enabled for QR, and only connected numbers can be picked.
 * `target` is what to send to the server: {} for the Meta way, { platform: "qr", connectionId } for QR.
 */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isQrNumberUsableState } from "../../../utils/qrNumberState";

export function useContactChannel(active = true) {
  const workspace = useSelector((s) => s.workspace?.currentWorkspace);
  const metaConnected = Boolean(workspace?.whatsappConnected);
  // A QR number linked to the workspace's own Meta coexistence number is never offered: its customers are the Meta
  // contacts, so a contact for it is added the official way (the server refuses it too).
  const numbers = useMemo(
    () =>
      workspace?.qrPilotEnabled && Array.isArray(workspace?.whatsappQrConnections)
        ? workspace.whatsappQrConnections.filter((n) => n.linkedToMeta !== true)
        : [],
    [workspace?.qrPilotEnabled, workspace?.whatsappQrConnections]
  );
  const hasQr = numbers.length > 0;
  // A number that is only reconnecting can still be chosen (the contact is created; sends queue); a logged-out one cannot.
  const connectedNumbers = numbers.filter((n) => isQrNumberUsableState(n.state));
  const showChoice = hasQr && metaConnected; // both exist -> a real choice between channels
  const qrOnly = hasQr && !metaConnected;

  const [channel, setChannel] = useState("meta");
  const [connectionId, setConnectionId] = useState("");

  // Defaults: QR when it is the only channel; the first connected number when none is chosen (or it went away).
  useEffect(() => {
    if (!active) return;
    if (qrOnly) setChannel("qr");
    else if (!hasQr) setChannel("meta");
  }, [active, qrOnly, hasQr]);
  useEffect(() => {
    if (!active) return;
    const valid = connectedNumbers.some((n) => n.connectionId === connectionId);
    if (!valid) setConnectionId(connectedNumbers[0]?.connectionId || "");
  }, [active, connectedNumbers, connectionId]);

  const usingQr = (qrOnly || channel === "qr") && hasQr;
  const ready = !usingQr || Boolean(connectionId); // QR needs a connected number to be chosen
  const target = usingQr ? { platform: "qr", connectionId } : {};

  return {
    showChoice,
    qrOnly,
    hasQr,
    numbers,
    channel: usingQr ? "qr" : "meta",
    setChannel,
    connectionId,
    setConnectionId,
    anyConnected: connectedNumbers.length > 0,
    usingQr,
    ready,
    target,
  };
}

export default useContactChannel;
